import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import moment from 'moment/moment';
import { approximateTimestamp } from '../utils/timestamp-utils';
import { Timestamp } from '../services/Timestamp';
import { useDispatch } from '../components/GlobalContextProvider/GlobalContextProvider';
import { ActionType } from '../components/GlobalContextProvider/global-reducer';
import { initStateFilters } from '../components/GlobalContextProvider/initial-trips-state';
import { Mode, type TimestampHandler } from '../types';

export const useManageTimestamps = (timestamp: string, mode: Mode, timezone: string): TimestampHandler => {
  const [liveTimestamp, setLiveTimestamp] = useState(Timestamp.now());
  const [playbackTimestamp, setPlaybackTimestamp] = useState(
    approximateTimestamp(Timestamp.fromSeconds(Number(timestamp)))
  );

  const dispatch = useDispatch();
  const handleSetPlaybackTimestamp = useCallback((timestamp?: Timestamp): void => {
    setPlaybackTimestamp(timestamp ? approximateTimestamp(timestamp) : null);
  }, []);

  useEffect(() => {
    const timeout = setInterval(() => {
      setLiveTimestamp(Timestamp.now());
    }, Timestamp.fromMinutes(1).toMilliseconds());

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const currentTimestamp = useMemo((): Timestamp => {
    return mode === Mode.Liveview || !playbackTimestamp ? liveTimestamp : playbackTimestamp;
  }, [mode, playbackTimestamp, liveTimestamp]);

  const currentDate = useMemo(() => {
    if (timezone) {
      const threshold = moment().tz(timezone).startOf('day');

      if (currentTimestamp.valueInMilliseconds < threshold.valueOf()) {
        return currentTimestamp.toLocationDateTimeOffset(timezone).dateString;
      }
    }

    return null;
  }, [currentTimestamp, timezone]);

  useLayoutEffect(() => {
    if (currentDate) {
      const date = moment(currentTimestamp.valueInMilliseconds).tz(timezone);
      const endDate = date.clone().endOf('day').toISOString();
      dispatch({ type: ActionType.UPDATE_END_DATE, payload: endDate });
    } else {
      dispatch({ type: ActionType.UPDATE_END_DATE, payload: initStateFilters.endDate });
    }
  }, [currentDate, mode]);

  return { playbackTimestamp, currentTimestamp, handleSetPlaybackTimestamp };
};
